import { useMemo } from "react";
import { useMediaAds } from "@b2bportal/core-media";
import { useDeviceType } from "@b2bportal/core-utilities";
import type {
  Ad,
  OfferTemplate,
  SearchTargeting,
} from "@b2bportal/media-ads-api";
import { AdType, ProductSearchParametersEnum } from "@b2bportal/media-ads-api";
import { Experiments, useExperiment } from "@/modules/experiments/src";
import { CANADA_COUNTRY_CODE } from "@/utilities/constants";

/** Deal-tile ads always carry the offer they load. */
export type AdWithOfferTemplate = Ad & { offerTemplate: OfferTemplate };

const hasOfferTemplate = (ad: Ad): ad is AdWithOfferTemplate =>
  ad.offerTemplate != null;

export enum HtsMediaDealTilePlacement {
  Homescreen = "Homescreen",
  DealsPage = "DealsPage",
  WalletPage = "TravelWallet",
}

// "global" market returns every active deal-tile ad.
const GLOBAL_SEARCH: SearchTargeting = {
  productSearchParameters: {
    ProductSearchParameters: ProductSearchParametersEnum.Hotel,
    marketName: "global",
  },
};

/** Sponsored deal tiles for a placement: claimable ads (with an offer template) and already-claimed offers. */
export const useHtsMediaDealTiles = (
  placement: HtsMediaDealTilePlacement,
  options: { enabled?: boolean } = {}
) => {
  const { isMobile } = useDeviceType();
  const { isExperimentVariant } = useExperiment();
  const flagEnabled = isExperimentVariant(
    Experiments.HTS_MEDIA.name,
    Experiments.HTS_MEDIA.variants.available
  );
  const enabled = flagEnabled && (options.enabled ?? true);

  const { state, reset } = useMediaAds({
    placementId: placement,
    adType: AdType.DealTile,
    countryCode: CANADA_COUNTRY_CODE,
    isMobile,
    enabled,
    search: GLOBAL_SEARCH,
  });

  const ads = useMemo<AdWithOfferTemplate[]>(
    () => (state.status === "loaded" ? state.ads.filter(hasOfferTemplate) : []),
    [state]
  );
  const claimedOffers = useMemo<OfferTemplate[]>(
    () => (state.status === "loaded" ? state.claimedOffers : []),
    [state]
  );

  return {
    ads,
    claimedOffers,
    isLoading: state.status === "loading",
    refetch: reset,
  };
};
